// MathJax integration for the article — ported from umag.html.
// Loads MathJax (tex-svg) from CDN once, configured with the custom \hv macro
// that wraps a variable in a hoverable <g class="hv-var hv-KEY">, then attaches
// tooltips that explain each variable.
//
// SPA hardening: MathJax's own auto-typeset is DISABLED (startup.typeset:false)
// and every typeset is funneled through a single serialized promise chain with
// typesetClear() first. Without this, two passes can run concurrently (React 19
// StrictMode double-invokes effects in dev; remounts on navigation add more) and
// MathJax throws "Cannot read properties of null (reading 'nextSibling')" when
// one pass mutates the DOM another is still walking.
import { useEffect, type RefObject } from 'react';

// key -> human-readable meaning shown on hover (second token of "hv-KEY").
const VAR_MEANINGS: Record<string, string> = {
  // Pair-strength formula
  sz1: 'szerva — csapat 1. tagja',
  sz2: 'szerva — csapat 2. tagja',
  a1: 'alapjáték — csapat 1. tagja',
  a2: 'alapjáték — csapat 2. tagja',
  r1: 'röpte — csapat 1. tagja',
  r2: 'röpte — csapat 2. tagja',
  bonusz: 'bónuszpontok összege (gyors, kiegyensúlyozott, ravasz)',
  buntet: 'büntetőpontok összege',
  // Élő rendszer
  Ea: 'a mérkőzés várható kimenetele A játékos szempontjából',
  Eb: 'a mérkőzés várható kimenetele B játékos szempontjából',
  Ra: 'A játékos Élő pontja a meccs előtt',
  Rb: 'B játékos Élő pontja a meccs előtt',
  RaP: 'A játékos új Élő pontja a meccs után',
  RbP: 'B játékos új Élő pontja a meccs után',
  Sa: 'a meccs tényleges eredménye A szempontjából (1, 0,5, 0)',
  K: 'K-faktor: az értékelés változásának sebessége',
};

const MATHJAX_SRC = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-svg.js';
const SCRIPT_ID = 'mathjax-script';

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    MathJax?: any;
  }
}

// Module-level: load MathJax exactly once, and serialize all typeset passes.
let loadPromise: Promise<void> | null = null;
let typesetChain: Promise<void> = Promise.resolve();

function loadMathJax(): Promise<void> {
  if (window.MathJax?.startup?.promise) return window.MathJax.startup.promise;
  if (loadPromise) return loadPromise;

  window.MathJax = {
    tex: {
      inlineMath: [
        ['$', '$'],
        ['\\(', '\\)'],
      ],
      displayMath: [
        ['$$', '$$'],
        ['\\[', '\\]'],
      ],
      processEscapes: true,
      tags: 'none',
      macros: {
        // \hv{key}{x} renders x inside <g class="hv-var hv-<key>"> for hover.
        hv: ['\\class{hv-var hv-#1}{#2}', 2],
      },
    },
    svg: { fontCache: 'global', scale: 1.05 },
    options: { skipHtmlTags: ['script', 'noscript', 'style', 'textarea', 'pre', 'code'] },
    // Do NOT auto-typeset the document; we drive it explicitly + serialized.
    startup: { typeset: false },
  };

  loadPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.async = true;
    s.src = MATHJAX_SRC;
    s.onload = () => {
      window.MathJax.startup.promise.then(() => resolve());
    };
    s.onerror = () => reject(new Error('MathJax failed to load'));
    document.head.appendChild(s);
  });
  return loadPromise;
}

export function useMathJax(
  containerRef: RefObject<HTMLElement | null>,
  tooltipRef: RefObject<HTMLDivElement | null>,
) {
  useEffect(() => {
    let cancelled = false;
    let hideTimer: ReturnType<typeof setTimeout> | null = null;

    function showTooltip(target: Element, text: string) {
      const tooltip = tooltipRef.current;
      if (!tooltip) return;
      tooltip.textContent = text;
      tooltip.classList.add('is-visible');
      tooltip.setAttribute('aria-hidden', 'false');
      const r = target.getBoundingClientRect();
      tooltip.style.left = `${r.left + r.width / 2}px`;
      tooltip.style.top = `${r.top}px`;
    }
    function hideTooltip() {
      const tooltip = tooltipRef.current;
      if (!tooltip) return;
      tooltip.classList.remove('is-visible');
      tooltip.setAttribute('aria-hidden', 'true');
    }

    function attachVarHover() {
      const root = containerRef.current;
      if (!root) return;
      root.querySelectorAll<SVGGElement>('mjx-container g.hv-var').forEach((g) => {
        const bound = g as unknown as { __bound?: boolean };
        if (bound.__bound) return;
        bound.__bound = true;
        let key: string | null = null;
        for (const c of Array.from(g.classList)) {
          if (c.startsWith('hv-') && c !== 'hv-var') {
            key = c.slice(3);
            break;
          }
        }
        if (!key) return;
        const meaning = VAR_MEANINGS[key];
        if (!meaning) return;

        g.addEventListener('mouseenter', () => {
          if (hideTimer) clearTimeout(hideTimer);
          showTooltip(g, meaning);
        });
        g.addEventListener('mouseleave', () => {
          hideTimer = setTimeout(hideTooltip, 80);
        });
        g.addEventListener('click', (e) => {
          e.stopPropagation();
          showTooltip(g, meaning);
          if (hideTimer) clearTimeout(hideTimer);
          hideTimer = setTimeout(hideTooltip, 2200);
        });
      });
    }

    loadMathJax()
      .then(() => {
        if (cancelled) return;
        // Enqueue this container's typeset onto the global serialized chain so
        // no two passes ever overlap.
        typesetChain = typesetChain.then(async () => {
          const root = containerRef.current;
          if (cancelled || !root || !window.MathJax?.typesetPromise) return;
          try {
            window.MathJax.typesetClear([root]);
            await window.MathJax.typesetPromise([root]);
            if (!cancelled) attachVarHover();
          } catch {
            // A stale/cancelled pass can still throw; safe to ignore.
          }
        });
      })
      .catch(() => {
        /* offline / CDN blocked — formulas stay as raw $…$ text, page still works */
      });

    const onScroll = () => hideTooltip();
    const onResize = () => hideTooltip();
    const onDocClick = () => hideTooltip();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    document.addEventListener('click', onDocClick);

    return () => {
      cancelled = true;
      if (hideTimer) clearTimeout(hideTimer);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('click', onDocClick);
    };
  }, [containerRef, tooltipRef]);
}
